import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataGrid, {
  Column,
  Paging,
  Pager,
  SearchPanel,
  HeaderFilter,
  Sorting,
  Toolbar as DGToolbar,
  Item as DGItem,
  LoadPanel,
} from 'devextreme-react/data-grid';
import { Popup } from 'devextreme-react/popup';
import { useAuth } from '../../contexts/auth';
import {
  getAllKBFiles,
  downloadFile,
  setS3CognitoUserCredentials,
} from '../../api/s3';
import './ai-knowledge-base.scss';
import { useNavigate } from 'react-router-dom';
import notify from 'devextreme/ui/notify';
import { Button } from 'devextreme-react/button';
import Accordion from 'devextreme-react/accordion';
import {
  getKBIngestionStatus,
  triggerKBIngestion,
  triggerSharePointSync,
  getSharePointSyncStatus,
} from '../../api/api';

const DOCUMENTS_SOURCE_ID = '8VGOBL4UP1';
const WEBSITE_SOURCE_ID = 'PIXWKNTQVK';

const parseReasons = (reasonStr) => {
  if (!reasonStr) return [];
  try {
    const parsed = typeof reasonStr === 'string' ? JSON.parse(reasonStr) : reasonStr;
    if (Array.isArray(parsed)) {
      return parsed.flatMap((p) => {
        if (typeof p === 'string' && (p.trim().startsWith('[') || p.trim().startsWith('{'))) {
          try {
            const inner = JSON.parse(p);
            if (Array.isArray(inner)) return inner;
          } catch (_) {}
        }
        return [p];
      });
    }
    if (typeof parsed === 'string') return [parsed];
  } catch (e) {
    console.error("Failed to parse failure reasons JSON:", e);
  }
  return [String(reasonStr)];
};

const parseFailureItem = (item) => {
  let docRef = '';
  let message = item;
  let type = 'system'; // 's3', 'web', 'system'

  if (typeof item !== 'string') {
    return { docRef: 'Unknown Error', message: String(item), type };
  }

  // 1. Web Crawler: Skipped document: <URL>. <Error>
  const skippedMatch = item.match(/^Skipped document:\s*([^\s]+)\s*(.*)$/i);
  if (skippedMatch) {
    docRef = skippedMatch[1];
    message = skippedMatch[2] || 'Document skipped';
    type = 'web';
  } else {
    // 2. S3 Documents: ... [Files: s3://...] ...
    const s3Match = item.match(/\[Files:\s*([^\]]+)\]/i);
    if (s3Match) {
      docRef = s3Match[1];
      message = item.replace(/\[Files:\s*[^\]]+\]/i, '').trim();
      type = 's3';
    } else {
      // 3. Fallback: URL or S3 detection
      const anyUrlMatch = item.match(/(https?:\/\/[^\s]+)/i);
      const anyS3Match = item.match(/(s3:\/\/[^\s\]]+)/i);
      if (anyUrlMatch) {
        docRef = anyUrlMatch[1];
        message = item.replace(anyUrlMatch[1], '').trim();
        type = 'web';
      } else if (anyS3Match) {
        docRef = anyS3Match[1];
        message = item.replace(anyS3Match[1], '').trim();
        type = 's3';
      }
    }
  }

  // Strip trailing punctuation like '.', ',', ';', ':', ')' frequently attached at end of matched URL
  if (docRef) {
    docRef = docRef.trim().replace(/[.,;:)]+$/, '');
  }

  if (docRef && type === 's3') {
    docRef = docRef.replace(/^s3:\/\/org\.axpx\.aiagentkb\/?/i, '');
  }

  if (docRef && type === 'web') {
    if (!docRef.startsWith('http://') && !docRef.startsWith('https://')) {
      docRef = 'https://' + docRef.replace(/^\/+/, '');
    }
  }

  message = message.replace(/^\.?\s*/, '').replace(/\.?\s*$/, '.');
  return { docRef, message, type };
};

const formatFileSize = (bytes) => {
  if (bytes === 0 || bytes === undefined || bytes === null) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getFileIconClass = (fileName) => {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  switch (ext) {
    case 'pdf':
      return 'far fa-file-pdf text-danger';
    case 'doc':
    case 'docx':
      return 'far fa-file-word text-primary';
    case 'xls':
    case 'xlsx':
    case 'csv':
      return 'far fa-file-excel text-success';
    case 'ppt':
    case 'pptx':
      return 'far fa-file-powerpoint text-warning';
    case 'txt':
      return 'far fa-file-alt text-secondary';
    default:
      return 'far fa-file text-muted';
  }
};

const formatDate = (dateVal) => {
  if (!dateVal) return '';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleString([], {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default function AIKnowledgeBasePage() {
  const { user, cognitoUserCredentials } = useAuth();
  const navigate = useNavigate();
  const [isAllowed, setIsAllowed] = useState(false);
  const [activeTab, setActiveTab] = useState('documents');

  const [files, setFiles] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  const [, setIngestionStatus] = useState(null);
  const [ingestionProgressData, setIngestionProgressData] = useState(null);
  const [isIngesting, setIsIngesting] = useState(false);
  const [isSyncingSharePoint, setIsSyncingSharePoint] = useState(false);
  const [sharePointSyncProgress, setSharePointSyncProgress] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [documentLastUpdated, setDocumentLastUpdated] = useState(null);
  const [websiteLastUpdated, setWebsiteLastUpdated] = useState(null);
  const [scannedItemsBySource, setScannedItemsBySource] = useState({ s3: [], web: [] });

  const [selectedFailedDoc, setSelectedFailedDoc] = useState(null);
  const [failedDocuments, setFailedDocuments] = useState([]);

  const formatLastUpdated = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch (e) {
      return isoString;
    }
  };

  const loadFiles = useCallback(async () => {
    setIsLoadingFiles(true);
    try {
      const list = await getAllKBFiles();
      setFiles(list || []);
    } catch (err) {
      console.error("Failed to load KB files from S3:", err);
      notify(
        {
          message: 'Failed to retrieve documents from storage.',
          position: { at: 'center top', my: 'center top' },
          type: 'error',
          displayTime: 4000,
        }
      );
    } finally {
      setIsLoadingFiles(false);
    }
  }, []);

  const checkIngestionStatus = useCallback(() => {
    if (!user) return;
    getKBIngestionStatus(
      user,
      (data) => {
        const status = data?.status || data?.jobStatus;
        const running = ['STARTING', 'IN_PROGRESS', 'RUNNING'].includes(status?.toUpperCase());

        if (data?.updatedAt) {
          setLastUpdated(data.updatedAt);
        }

        if (data?.by_source?.s3?.updatedAt) {
          setDocumentLastUpdated(data.by_source.s3.updatedAt);
        } else if (data?.dataSourceId === DOCUMENTS_SOURCE_ID && data?.updatedAt) {
          setDocumentLastUpdated(data.updatedAt);
        }

        if (data?.by_source?.web?.updatedAt) {
          setWebsiteLastUpdated(data.by_source.web.updatedAt);
        } else if (data?.dataSourceId === WEBSITE_SOURCE_ID && data?.updatedAt) {
          setWebsiteLastUpdated(data.updatedAt);
        }

        if (data?.failedDocuments) {
          setFailedDocuments(data.failedDocuments);
        }

        setIsIngesting((prevIsIngesting) => {
          if (prevIsIngesting && !running) {
            if (status?.toUpperCase() === 'COMPLETE') {
              notify(
                {
                  message: 'KB Ingestion job completed successfully!',
                  position: { at: 'center top', my: 'center top' },
                  type: 'success',
                  displayTime: 5000,
                }
              );
              loadFiles();
            } else if (status?.toUpperCase() === 'FAILED') {
              notify(
                {
                  message: 'KB Ingestion job failed.',
                  position: { at: 'center top', my: 'center top' },
                  type: 'error',
                  displayTime: 5000,
                }
              );
            }
          }
          return running;
        });

        setIngestionStatus(status);
        const progressObj = data?.progress ? { ...data.progress } : null;
        if (progressObj && data?.by_source && !progressObj.by_source) {
          progressObj.by_source = data.by_source;
        }
        setIngestionProgressData(progressObj);
        if (data?.scanned_items_by_source) {
          setScannedItemsBySource(data.scanned_items_by_source);
        }
      },
      (error) => {
        console.error("Failed to fetch KB ingestion status:", error);
      }
    );
  }, [user, loadFiles]);

  const checkSharePointSyncStatus = useCallback(() => {
    if (!user) return;
    getSharePointSyncStatus(
      user,
      (data) => {
        const spStatus = (data?.sharepoint_status || data?.sharepoint_sync?.status || data?.status || '').toUpperCase();
        const spRunning = spStatus === 'IN_PROGRESS' || spStatus === 'STARTING' || spStatus === 'RUNNING';
        const stats = data?.stats || data?.sharepoint_sync?.stats || data?.result?.stats || {};

        if (spRunning) {
          setSharePointSyncProgress({
            current_source_name: 'SharePoint to S3',
            percent: 30,
            statistics: {
              numberOfDocumentsScanned: stats.total_found ?? 0,
              numberOfNewDocumentsIndexed: stats.uploaded ?? 0,
              numberOfDocumentsFailed: stats.failed ?? 0,
            },
            step_details: [
              { source_id: 'sharepoint', name: 'SharePoint to S3', status: 'IN_PROGRESS' },
              { source_id: DOCUMENTS_SOURCE_ID, name: 'Documents KB Index', status: 'PENDING' },
            ],
          });
        }

        setIsSyncingSharePoint((prevRunning) => {
          if (prevRunning && !spRunning) {
            if (spStatus === 'COMPLETED' || spStatus === 'PARTIAL_SUCCESS') {
              loadFiles();
              setSharePointSyncProgress(null);
              if (data?.result?.kb_ingestion_triggered || data?.sharepoint_sync?.result?.kb_ingestion_triggered) {
                setIsIngesting(true);
                checkIngestionStatus();
              } else {
                triggerKBIngestion(
                  user,
                  { data_source_id: DOCUMENTS_SOURCE_ID },
                  () => {
                    setIsIngesting(true);
                    checkIngestionStatus();
                  },
                  (err) => {
                    console.error('Failed to trigger Documents KB ingestion after SharePoint sync:', err);
                    notify(
                      {
                        message: 'SharePoint files synced to S3, but failed to start KB index job.',
                        position: { at: 'center top', my: 'center top' },
                        type: 'warning',
                        displayTime: 5000,
                      }
                    );
                  }
                );
              }
            } else if (spStatus === 'FAILED') {
              setSharePointSyncProgress(null);
              notify(
                {
                  message: data?.error || data?.sharepoint_sync?.error || 'SharePoint synchronization failed.',
                  position: { at: 'center top', my: 'center top' },
                  type: 'error',
                  displayTime: 5000,
                }
              );
            }
          }
          return spRunning;
        });
      },
      (error) => {
        // 404 simply means no SharePoint sync job has been run yet
        if (error?.status !== 404) {
          console.error('Failed to fetch SharePoint sync status:', error);
        }
      }
    );
  }, [user, loadFiles, checkIngestionStatus]);

  const handleSyncDocuments = () => {
    if (!user) return;
    setActiveTab('documents');
    setIsIngesting(true);
    setIngestionProgressData({
      current_source_name: 'Documents',
      percent: 15,
      statistics: {
        numberOfDocumentsScanned: 0,
        numberOfNewDocumentsIndexed: 0,
        numberOfDocumentsFailed: 0,
      },
      step_details: [
        { source_id: DOCUMENTS_SOURCE_ID, name: 'Documents', status: 'IN_PROGRESS' },
      ],
    });

    triggerKBIngestion(
      user,
      { data_source_id: DOCUMENTS_SOURCE_ID },
      () => {
        notify(
          {
            message: 'Documents KB Sync job started successfully.',
            position: { at: 'center top', my: 'center top' },
            type: 'success',
            displayTime: 3000,
          }
        );
        checkIngestionStatus();
      },
      (error) => {
        console.error('Failed to trigger Documents KB Sync:', error);
        setIsIngesting(false);
        notify(
          {
            message: 'Failed to trigger Documents KB Sync job.',
            position: { at: 'center top', my: 'center top' },
            type: 'error',
            displayTime: 5000,
          }
        );
      }
    );
  };

  const handleSyncWebsite = () => {
    if (!user) return;
    setActiveTab('website');
    setIsIngesting(true);
    setIngestionProgressData({
      current_source_name: 'AXPC Website',
      percent: 15,
      statistics: {
        numberOfDocumentsScanned: 0,
        numberOfNewDocumentsIndexed: 0,
        numberOfDocumentsFailed: 0,
      },
      step_details: [
        { source_id: WEBSITE_SOURCE_ID, name: 'AXPC Website', status: 'IN_PROGRESS' },
      ],
    });

    triggerKBIngestion(
      user,
      { data_source_id: WEBSITE_SOURCE_ID },
      () => {
        notify(
          {
            message: 'Website KB Sync job started successfully.',
            position: { at: 'center top', my: 'center top' },
            type: 'success',
            displayTime: 3000,
          }
        );
        checkIngestionStatus();
      },
      (error) => {
        console.error('Failed to trigger Website KB Sync:', error);
        setIsIngesting(false);
        notify(
          {
            message: 'Failed to trigger Website KB Sync job.',
            position: { at: 'center top', my: 'center top' },
            type: 'error',
            displayTime: 5000,
          }
        );
      }
    );
  };


  const handleRefreshAll = () => {
    loadFiles();
    checkSharePointSyncStatus();
    checkIngestionStatus();
  };

  useEffect(() => {
    if (user && isAllowed) {
      checkSharePointSyncStatus();
      checkIngestionStatus();
      loadFiles();
    }
  }, [user, isAllowed, checkSharePointSyncStatus, checkIngestionStatus, loadFiles]);

  useEffect(() => {
    let intervalId;
    if (user && isAllowed && isSyncingSharePoint) {
      intervalId = setInterval(() => {
        checkSharePointSyncStatus();
      }, 4000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [user, isAllowed, isSyncingSharePoint, checkSharePointSyncStatus]);

  useEffect(() => {
    let intervalId;
    if (user && isAllowed && isIngesting) {
      intervalId = setInterval(() => {
        checkIngestionStatus();
      }, 5000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [user, isAllowed, isIngesting, checkIngestionStatus]);

  useEffect(() => {
    if (user && cognitoUserCredentials) {
      const groups = user.signInUserSession.idToken.payload['cognito:groups'];
      let userIsAllowed = false;

      if (groups) {
        if (groups.includes('admin') || groups.some((group) => group.includes('kbmanager'))) {
          userIsAllowed = true;
        }
      }

      if (userIsAllowed) {
        setS3CognitoUserCredentials(
          cognitoUserCredentials,
          user.signInUserSession.getIdToken().payload.sub
        );
        setIsAllowed(true);
      } else {
        navigate('/home');
        notify(
          {
            message: 'You are not authorized to view this page.',
            position: { at: 'center top', my: 'center top' },
            type: 'error',
            displayTime: 3000,
          }
        );
      }
    }
  }, [user, cognitoUserCredentials, navigate]);

  const failuresMap = useMemo(() => {
    const map = {};
    if (!failedDocuments || failedDocuments.length === 0) return map;
    failedDocuments.forEach((doc) => {
      const rawReasons = parseReasons(doc.reason);
      const parsedItems = rawReasons.map(parseFailureItem);
      parsedItems.forEach((item) => {
        if (item.docRef) {
          const cleanRef = item.docRef.replace(/^\/+/, '').toLowerCase();
          map[cleanRef] = item;
          const baseName = cleanRef.split('/').pop();
          if (baseName && !map[baseName]) {
            map[baseName] = item;
          }
        }
      });
    });
    return map;
  }, [failedDocuments]);

  const isSyncingDocuments = useMemo(() => {
    if (!isIngesting) return false;

    if (ingestionProgressData?.step_details && Array.isArray(ingestionProgressData.step_details)) {
      const docStep = ingestionProgressData.step_details.find(
        (step) => step.source_id === DOCUMENTS_SOURCE_ID || (step.name && step.name.toLowerCase().includes('document'))
      );
      if (docStep) {
        const stepStatus = (docStep.status || '').toUpperCase();
        return ['IN_PROGRESS', 'STARTING', 'RUNNING'].includes(stepStatus);
      }
    }

    const currentName = (ingestionProgressData?.current_source_name || '').toLowerCase();
    const currentSource = ingestionProgressData?.current_source || ingestionProgressData?.dataSourceId;

    if (currentSource === DOCUMENTS_SOURCE_ID) {
      return true;
    }
    if (currentName.includes('document')) {
      return true;
    }

    return false;
  }, [isIngesting, ingestionProgressData]);

  const isSyncingWebsite = useMemo(() => {
    if (!isIngesting) return false;

    if (ingestionProgressData?.step_details && Array.isArray(ingestionProgressData.step_details)) {
      const webStep = ingestionProgressData.step_details.find(
        (step) => step.source_id === WEBSITE_SOURCE_ID || (step.name && step.name.toLowerCase().includes('website'))
      );
      if (webStep) {
        const stepStatus = (webStep.status || '').toUpperCase();
        return ['IN_PROGRESS', 'STARTING', 'RUNNING'].includes(stepStatus);
      }
    }

    const currentName = (ingestionProgressData?.current_source_name || '').toLowerCase();
    const currentSource = ingestionProgressData?.current_source || ingestionProgressData?.dataSourceId;

    if (currentSource === WEBSITE_SOURCE_ID) {
      return true;
    }
    if (currentName.includes('website') || currentName.includes('web')) {
      return true;
    }

    return false;
  }, [isIngesting, ingestionProgressData]);

  const sourceSyncDetails = useMemo(() => {
    const bySource = ingestionProgressData?.by_source || {};

    // 1. Documents (S3)
    const s3Meta = bySource.s3 || bySource[DOCUMENTS_SOURCE_ID] || {};
    const s3Items = scannedItemsBySource?.s3 || [];
    const s3Scanned = s3Meta.statistics?.numberOfDocumentsScanned ?? (s3Items.length || 0);
    const s3Indexed = s3Meta.statistics?.numberOfNewDocumentsIndexed ?? s3Items.filter((i) => (i.status || '').toUpperCase() === 'INDEXED').length;
    const s3Failed = s3Meta.statistics?.numberOfDocumentsFailed ?? s3Items.filter((i) => (i.status || '').toUpperCase() === 'FAILED').length;

    let s3Time = s3Meta.updatedAt;
    if (!s3Time && s3Items.length > 0) {
      const times = s3Items.map((i) => i.updated_at).filter(Boolean);
      if (times.length > 0) {
        times.sort();
        s3Time = times[times.length - 1];
      }
    }
    if (!s3Time && documentLastUpdated) {
      s3Time = documentLastUpdated;
    }

    // 2. Website (Web)
    const webMeta = bySource.web || bySource[WEBSITE_SOURCE_ID] || {};
    const webItems = scannedItemsBySource?.web || [];
    const webScanned = webMeta.statistics?.numberOfDocumentsScanned ?? (webItems.length || 0);
    const webIndexed = webMeta.statistics?.numberOfNewDocumentsIndexed ?? webItems.filter((i) => (i.status || '').toUpperCase() === 'INDEXED').length;
    const webFailed = webMeta.statistics?.numberOfDocumentsFailed ?? webItems.filter((i) => (i.status || '').toUpperCase() === 'FAILED').length;

    let webTime = webMeta.updatedAt;
    if (!webTime && webItems.length > 0) {
      const times = webItems.map((i) => i.updated_at).filter(Boolean);
      if (times.length > 0) {
        times.sort();
        webTime = times[times.length - 1];
      }
    }
    if (!webTime && websiteLastUpdated) {
      webTime = websiteLastUpdated;
    }

    return {
      documents: {
        id: 'stats-accordion-documents',
        title: 'Documents',
        type: 's3',
        icon: 'far fa-file-alt',
        scannedLabel: 'Documents Scanned',
        lastUpdated: s3Time,
        stats: {
          numberOfDocumentsScanned: s3Scanned,
          numberOfNewDocumentsIndexed: s3Indexed,
          numberOfDocumentsFailed: s3Failed,
        },
      },
      website: {
        id: 'stats-accordion-website',
        title: 'Website Links',
        type: 'web',
        icon: 'fas fa-globe',
        scannedLabel: 'Website Links Scanned',
        lastUpdated: webTime,
        stats: {
          numberOfDocumentsScanned: webScanned,
          numberOfNewDocumentsIndexed: webIndexed,
          numberOfDocumentsFailed: webFailed,
        },
      },
    };
  }, [ingestionProgressData, scannedItemsBySource, documentLastUpdated, websiteLastUpdated]);

  const documentProgressData = useMemo(() => {
    if (!isSyncingDocuments) return null;

    const docStats = sourceSyncDetails.documents?.stats || {};
    const s3Meta = ingestionProgressData?.by_source?.s3 || ingestionProgressData?.by_source?.[DOCUMENTS_SOURCE_ID];
    const stats = s3Meta?.statistics ||
      (ingestionProgressData?.current_source_name?.toLowerCase().includes('document') ? ingestionProgressData?.statistics : null) || {
        numberOfDocumentsScanned: docStats.numberOfDocumentsScanned ?? 0,
        numberOfNewDocumentsIndexed: docStats.numberOfNewDocumentsIndexed ?? 0,
        numberOfDocumentsFailed: docStats.numberOfDocumentsFailed ?? 0,
      };

    return {
      current_source_name: 'Documents',
      percent: ingestionProgressData?.percent || 25,
      statistics: stats,
      step_details: [
        {
          source_id: DOCUMENTS_SOURCE_ID,
          name: 'Documents Data Source',
          status: 'IN_PROGRESS',
        },
      ],
    };
  }, [isSyncingDocuments, ingestionProgressData, sourceSyncDetails.documents]);

  const websiteProgressData = useMemo(() => {
    if (!isSyncingWebsite) return null;

    const webStats = sourceSyncDetails.website?.stats || {};
    const webMeta = ingestionProgressData?.by_source?.web || ingestionProgressData?.by_source?.[WEBSITE_SOURCE_ID];
    const stats = webMeta?.statistics ||
      (ingestionProgressData?.current_source_name?.toLowerCase().includes('website') ? ingestionProgressData?.statistics : null) || {
        numberOfDocumentsScanned: webStats.numberOfDocumentsScanned ?? 0,
        numberOfNewDocumentsIndexed: webStats.numberOfNewDocumentsIndexed ?? 0,
        numberOfDocumentsFailed: webStats.numberOfDocumentsFailed ?? 0,
      };

    return {
      current_source_name: 'AXPC Website',
      percent: ingestionProgressData?.percent || 25,
      statistics: stats,
      step_details: [
        {
          source_id: WEBSITE_SOURCE_ID,
          name: 'AXPC Website',
          status: 'IN_PROGRESS',
        },
      ],
    };
  }, [isSyncingWebsite, ingestionProgressData, sourceSyncDetails.website]);

  const enrichedFiles = useMemo(() => {
    const docLastSync = sourceSyncDetails.documents?.lastUpdated;
    const lastSyncTime = docLastSync ? new Date(docLastSync).getTime() : 0;
    const s3ScannedMap = new Map();
    if (scannedItemsBySource?.s3 && scannedItemsBySource.s3.length > 0) {
      scannedItemsBySource.s3.forEach((item) => {
        const k = (item.key || '').toLowerCase();
        const n = (item.name || '').toLowerCase();
        if (k) s3ScannedMap.set(k, item);
        if (n) s3ScannedMap.set(n, item);
      });
    }

    return files.map((file) => {
      const keyLower = (file.key || '').toLowerCase();
      const nameLower = (file.name || '').toLowerCase();
      const failure =
        failuresMap[keyLower] ||
        failuresMap[nameLower] ||
        Object.entries(failuresMap).find(([refKey]) =>
          keyLower.endsWith(refKey) || refKey.endsWith(keyLower)
        )?.[1];
      const s3Scanned =
        s3ScannedMap.get(keyLower) ||
        s3ScannedMap.get(nameLower) ||
        Array.from(s3ScannedMap.entries()).find(([refKey]) =>
          keyLower.endsWith(refKey) || refKey.endsWith(keyLower)
        )?.[1];

      let status = 'INDEXED';
      let statusLabel = 'Indexed';
      let statusReason = null;

      if (failure) {
        status = 'FAILED';
        statusLabel = 'Failed';
        statusReason = failure.message || 'Ingestion failed for this document';
      } else if (s3Scanned && s3Scanned.status === 'FAILED') {
        status = 'FAILED';
        statusLabel = 'Failed';
        statusReason = s3Scanned.status_reason || 'Ingestion failed for this document';
      } else if (isSyncingDocuments) {
        status = 'SYNCING';
        statusLabel = 'Syncing...';
      } else if (s3Scanned && s3Scanned.status === 'INDEXED') {
        status = 'INDEXED';
        statusLabel = 'Indexed';
        statusReason = s3Scanned.status_reason || 'Indexed in Knowledge Base';
      } else {
        const fileModTime = file.lastModified ? new Date(file.lastModified).getTime() : 0;
        if (!lastSyncTime || fileModTime > lastSyncTime) {
          status = 'PENDING';
          statusLabel = 'Pending Sync';
          statusReason = 'Modified after last sync; awaiting next sync run';
        } else {
          status = 'INDEXED';
          statusLabel = 'Indexed';
        }
      }

      return {
        ...file,
        computedStatus: status,
        computedStatusLabel: statusLabel,
        statusReason: statusReason,
      };
    }).sort((a, b) => {
      const aFailed = a.computedStatus === 'FAILED' ? 0 : 1;
      const bFailed = b.computedStatus === 'FAILED' ? 0 : 1;
      if (aFailed !== bFailed) {
        return aFailed - bFailed;
      }
      const aTime = a.lastModified ? new Date(a.lastModified).getTime() : 0;
      const bTime = b.lastModified ? new Date(b.lastModified).getTime() : 0;
      return bTime - aTime;
    });
  }, [files, failuresMap, isSyncingDocuments, sourceSyncDetails.documents?.lastUpdated, scannedItemsBySource]);

  const enrichedWebsiteLinks = useMemo(() => {
    const webItemsMap = new Map();

    const normalizeUrl = (u) => {
      if (!u) return '';
      let clean = u.trim().replace(/[.,;:)]+$/, '');
      if (!clean.startsWith('http://') && !clean.startsWith('https://')) {
        clean = 'https://' + clean.replace(/^\/+/, '');
      }
      return clean;
    };

    const getDomain = (u) => {
      try {
        return new URL(u).hostname.replace(/^www\./, '');
      } catch (e) {
        return u.split('/')[2] || 'axpc.org';
      }
    };

    const formatTitleFromUrl = (u) => {
      try {
        const parsed = new URL(u);
        const segments = parsed.pathname.split('/').filter(Boolean);
        if (segments.length === 0) {
          return parsed.hostname.replace(/^www\./, '') + ' (Homepage)';
        }
        const last = segments[segments.length - 1];
        return last
          .replace(/[-_]+/g, ' ')
          .replace(/\.pdf$/i, ' (PDF)')
          .replace(/\.html?$/i, '')
          .replace(/\b\w/g, (c) => c.toUpperCase());
      } catch (e) {
        return u;
      }
    };

    // 1. First populate scanned items directly reported from /kb/ingestion/status
    if (scannedItemsBySource?.web && scannedItemsBySource.web.length > 0) {
      scannedItemsBySource.web.forEach((item) => {
        let url = item.url || item.uri;
        if (url) {
          url = normalizeUrl(url);
          const domain = item.domain || getDomain(url);
          const key = url.toLowerCase().replace(/\/+$/, '');
          webItemsMap.set(key, {
            url,
            domain,
            title: item.name || formatTitleFromUrl(url),
            statusReason: item.status_reason || item.statusReason,
            isFailed: (item.status || '').toUpperCase() === 'FAILED',
            type: 'web',
            lastModified: item.updated_at || item.lastModified || sourceSyncDetails.website?.lastUpdated || null,
          });
        }
      });
    }

    // 2. Extract and merge all web items and failure reasons from failedDocuments (from sync job)
    if (failedDocuments && failedDocuments.length > 0) {
      failedDocuments.forEach((doc) => {
        const isWebJob =
          doc.name?.toLowerCase().includes('website') ||
          doc.name?.toLowerCase().includes('web') ||
          doc.path?.toLowerCase().includes('website');
        const rawReasons = parseReasons(doc.reason);
        const parsedItems = rawReasons.map(parseFailureItem);

        parsedItems.forEach((item) => {
          if (item.type === 'web' || isWebJob) {
            let url = item.docRef;
            if (url) {
              url = normalizeUrl(url);
              const domain = getDomain(url);
              const key = url.toLowerCase().replace(/\/+$/, '');
              const existing = webItemsMap.get(key);

              webItemsMap.set(key, {
                url,
                domain,
                title: existing?.title || formatTitleFromUrl(url),
                statusReason: item.message,
                isFailed: true,
                type: 'web',
                lastModified: doc.updatedAt || sourceSyncDetails.website?.lastUpdated || null,
              });
            }
          }
        });
      });
    }

    return Array.from(webItemsMap.values()).map((link) => {
      let status = 'INDEXED';
      let statusLabel = 'Indexed';
      let reason = link.statusReason || 'Successfully indexed by web crawler';

      if (link.isFailed) {
        status = 'FAILED';
        statusLabel = 'Failed';
        reason = link.statusReason || 'Crawler failed or skipped this page';
      } else if (isSyncingWebsite) {
        status = 'SYNCING';
        statusLabel = 'Syncing...';
        reason = 'Web crawler ingestion in progress';
      }

      return {
        ...link,
        computedStatus: status,
        computedStatusLabel: statusLabel,
        statusReason: reason,
        key: link.url,
        name: link.title || link.url,
      };
    }).sort((a, b) => {
      const aFailed = a.computedStatus === 'FAILED' ? 0 : 1;
      const bFailed = b.computedStatus === 'FAILED' ? 0 : 1;
      if (aFailed !== bFailed) {
        return aFailed - bFailed;
      }
      return a.url.localeCompare(b.url);
    });
  }, [scannedItemsBySource, failedDocuments, isSyncingWebsite, sourceSyncDetails.website?.lastUpdated]);

  const nameCellRender = (data) => {
    const file = data.data;
    return (
      <div className="kb-file-name-cell">
        <i className={`${getFileIconClass(file.name)} kb-file-icon`} />
        <a
          href="#download"
          className="kb-file-link"
          onClick={(e) => {
            e.preventDefault();
            downloadFile(file.key, file.name);
          }}
          title={`Click to download ${file.name}`}
        >
          {file.name}
        </a>
      </div>
    );
  };

  const folderCellRender = (data) => {
    const folder = data.value || '/';
    return (
      <span className="kb-folder-cell" title={folder}>
        <i className="far fa-folder" style={{ color: '#faad14' }} />
        <span>{folder}</span>
      </span>
    );
  };

  const handleStatusClick = (e, item) => {
    e.stopPropagation();
    if (item.computedStatus === 'FAILED') {
      setSelectedFailedDoc(item);
    }
  };

  const statusCellRender = (data) => {
    const item = data.data;
    const status = item.computedStatus;
    const label = item.computedStatusLabel;
    const reason = item.statusReason;
    const isFailed = status === 'FAILED';

    let iconClass = 'far fa-check-circle';
    let badgeClass = 'status-indexed';

    if (status === 'SYNCING') {
      iconClass = 'fas fa-sync-alt fa-spin';
      badgeClass = 'status-syncing';
    } else if (status === 'PENDING') {
      iconClass = 'far fa-clock';
      badgeClass = 'status-pending';
    } else if (status === 'FAILED') {
      iconClass = 'fas fa-exclamation-triangle';
      badgeClass = 'status-failed';
    }

    return (
      <span
        className={`kb-status-badge ${badgeClass} ${isFailed ? 'clickable' : ''}`}
        title={isFailed ? `${reason || label} (Click for details)` : (reason || label)}
        onClick={isFailed ? (e) => handleStatusClick(e, item) : undefined}
        role={isFailed ? 'button' : undefined}
      >
        <i className={iconClass} />
        <span>{label}</span>
      </span>
    );
  };

  const lastModifiedCellRender = (data) => {
    return <span>{formatDate(data.value)}</span>;
  };

  const sizeCellRender = (data) => {
    return <span>{formatFileSize(data.value)}</span>;
  };

  const actionCellRender = (data) => {
    const file = data.data;
    return (
      <button
        type="button"
        className="btn btn-sm kb-download-btn"
        onClick={() => downloadFile(file.key, file.name)}
        title="Download file"
      >
        <i className="fas fa-download" />
      </button>
    );
  };

  const urlCellRender = (data) => {
    const link = data.data;
    const title = link.title;
    return (
      <div className="kb-web-link-cell">
        <i className="fas fa-globe kb-web-icon" />
        <div className="kb-web-link-text">
          {title && title !== link.url && (
            <span className="kb-web-title" title={title}>{title}</span>
          )}
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="kb-web-link"
            title={`Open ${link.url} in new tab`}
          >
            <span>{link.url}</span>
            <i className="fas fa-external-link-alt kb-external-icon" />
          </a>
        </div>
      </div>
    );
  };

  const domainCellRender = (data) => {
    const domain = data.value || '';
    return (
      <span className="kb-domain-badge">
        <i className="fas fa-globe-americas" />
        <span>{domain}</span>
      </span>
    );
  };

  const webActionCellRender = (data) => {
    const link = data.data;
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-sm kb-download-btn"
        title="Open link in new tab"
      >
        <i className="fas fa-external-link-alt" />
      </a>
    );
  };

  const renderDocErrorContent = useCallback(() => {
    if (!selectedFailedDoc) return null;
    const isWeb = selectedFailedDoc.type === 'web' || !!selectedFailedDoc.url;
    return (
      <div className="doc-error-popup-container">
        <div className="doc-error-header">
          <div className="doc-error-icon-box">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="doc-error-file-info">
            <h4 className="doc-error-filename">
              {selectedFailedDoc.url || selectedFailedDoc.name}
            </h4>
            <span
              className="doc-error-filepath"
              title={selectedFailedDoc.url || selectedFailedDoc.key}
            >
              {selectedFailedDoc.url || selectedFailedDoc.key}
            </span>
          </div>
        </div>

        <div className="doc-error-body">
          <span className="doc-error-heading">Error Detail:</span>
          <div className="doc-error-message">
            {selectedFailedDoc.statusReason || 'Ingestion failed for this item.'}
          </div>
        </div>

        <div className="doc-error-meta-grid">
          {selectedFailedDoc.domain && (
            <div className="doc-error-meta-item">
              <span className="meta-label">Domain</span>
              <span className="meta-val">{selectedFailedDoc.domain}</span>
            </div>
          )}
          {selectedFailedDoc.folder && (
            <div className="doc-error-meta-item">
              <span className="meta-label">Folder</span>
              <span className="meta-val">{selectedFailedDoc.folder}</span>
            </div>
          )}
          {selectedFailedDoc.lastModified && (
            <div className="doc-error-meta-item">
              <span className="meta-label">{isWeb ? 'Last Crawled' : 'Last Modified'}</span>
              <span className="meta-val">{formatDate(selectedFailedDoc.lastModified)}</span>
            </div>
          )}
          {selectedFailedDoc.size !== undefined && selectedFailedDoc.size !== null && (
            <div className="doc-error-meta-item">
              <span className="meta-label">Size</span>
              <span className="meta-val">{formatFileSize(selectedFailedDoc.size)}</span>
            </div>
          )}
        </div>

        <div className="doc-error-actions">
          {isWeb ? (
            <a
              href={selectedFailedDoc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <i className="fas fa-external-link-alt"></i>
              <span>Open Link</span>
            </a>
          ) : (
            <Button
              text="Download File"
              icon="fas fa-download"
              type="default"
              stylingMode="outlined"
              onClick={() => downloadFile(selectedFailedDoc.key, selectedFailedDoc.name)}
            />
          )}
          <Button
            text="Close"
            stylingMode="contained"
            onClick={() => setSelectedFailedDoc(null)}
          />
        </div>
      </div>
    );
  }, [selectedFailedDoc]);

  const userGroups = user?.signInUserSession?.idToken?.payload['cognito:groups'] || [];
  const canSync = userGroups.includes('admin') || userGroups.some(g => g.includes('kbmanager'));

  const isSyncBusy = isIngesting || isSyncingSharePoint;

  const renderTabSyncStatus = (sourceItem, isSyncingThisSource) => {
    if (!sourceItem || isSyncingThisSource) {
      return null;
    }
    return (
      <div className="kb-last-sync-container">
        <Accordion
          key={sourceItem.id}
          dataSource={[sourceItem]}
          collapsible={true}
          multiple={false}
          defaultSelectedIndex={-1}
          itemTitleRender={() => (
            <div className="kb-last-sync-accordion-title">
              
              <span className="kb-stats-toggle-label">
                <i className="far fa-clock kb-sync-time-icon"></i>
                Last sync:{' '}
                <span className="kb-last-sync-time">
                  {sourceItem.lastUpdated ? formatLastUpdated(sourceItem.lastUpdated) : 'Never'}
                </span>
              </span>
            </div>
          )}
          itemRender={() => (
            <div className="kb-completed-stats-grid">
              <div className="completed-stat-card scanned">
                <div className="completed-stat-icon-wrapper">
                  <i className={sourceItem.icon}></i>
                </div>
                <div className="completed-stat-content">
                  <span className="completed-stat-label">{sourceItem.scannedLabel}</span>
                  <span className="completed-stat-value">
                    {sourceItem.stats?.numberOfDocumentsScanned ?? 0}
                  </span>
                </div>
              </div>

              <div className="completed-stat-card indexed">
                <div className="completed-stat-icon-wrapper">
                  <i className="far fa-check-circle"></i>
                </div>
                <div className="completed-stat-content">
                  <span className="completed-stat-label">Successfully Indexed</span>
                  <span className="completed-stat-value">
                    {sourceItem.stats?.numberOfNewDocumentsIndexed ?? 0}
                  </span>
                </div>
              </div>

              <div className="completed-stat-card failed">
                <div className="completed-stat-icon-wrapper">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <div className="completed-stat-content">
                  <span className="completed-stat-label">Sync Errors / Failed</span>
                  <span className="completed-stat-value">
                    {sourceItem.stats?.numberOfDocumentsFailed ?? 0}
                  </span>
                </div>
              </div>
            </div>
          )}
        />
      </div>
    );
  };

  const renderSyncProgressCard = (progressData) => (
    <div className="kb-sync-progress-card">
      <div className="kb-sync-header">
        <div className="kb-sync-title-container">
          <i className="fas fa-sync-alt kb-sync-spinner"></i>
          <h3 className="kb-sync-title">
            {progressData?.current_source_name 
              ? `Syncing (${progressData.current_source_name})...` 
              : "Syncing Knowledge Base data..."}
          </h3>
        </div>
        <div className="kb-sync-percent">
          {progressData ? `${progressData.percent}%` : "Connecting..."}
        </div>
      </div>

      <div className="kb-sync-progress-bar-container">
        <div 
          className="kb-sync-progress-bar" 
          style={{ width: `${progressData ? progressData.percent : 5}%` }}
        ></div>
      </div>

      <div className="kb-sync-details">
        <div className="kb-sync-stat-item scanned">
          <span className="stat-label">Scanned</span>
          <span className="stat-value">
            {progressData?.statistics?.numberOfDocumentsScanned ?? 0}
          </span>
        </div>
        <div className="kb-sync-stat-item indexed">
          <span className="stat-label">Successfully Indexed</span>
          <span className="stat-value">
            {progressData?.statistics?.numberOfNewDocumentsIndexed ?? 0}
          </span>
        </div>
        <div className="kb-sync-stat-item failed">
          <span className="stat-label">Sync Errors / Failed</span>
          <span className="stat-value">
            {progressData?.statistics?.numberOfDocumentsFailed ?? 0}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ai-knowledge-base-container">
      <div className="content-block">
        <h2 style={{ margin: 0 }}>AI Knowledge Base</h2>
      </div>

      {isAllowed && (
        <div className={'content-block kb-table-container'}>
          <div className="kb-tabs-header">
            <div className="kb-source-tabs">
              <button
                type="button"
                className={`kb-source-tab ${activeTab === 'documents' ? 'active' : ''}`}
                onClick={() => setActiveTab('documents')}
              >
                <i className="far fa-file-alt" />
                <span>Documents</span>
                <span className="kb-tab-badge">{enrichedFiles.length}</span>
                {isSyncingDocuments && (
                  <i className="fas fa-spinner fa-spin kb-tab-sync-spinner" title="Syncing documents..." />
                )}
              </button>
              <button
                type="button"
                className={`kb-source-tab ${activeTab === 'website' ? 'active' : ''}`}
                onClick={() => setActiveTab('website')}
              >
                <i className="fas fa-globe" />
                <span>Website Links</span>
                <span className="kb-tab-badge">{enrichedWebsiteLinks.length}</span>
                {isSyncingWebsite && (
                  <i className="fas fa-spinner fa-spin kb-tab-sync-spinner" title="Syncing website links..." />
                )}
              </button>
            </div>
          </div>

          {activeTab === 'documents' ? (
            <div className="kb-tab-content">
              <div className="kb-tab-toolbar">
                <div className="kb-tab-status-area">
                  {renderTabSyncStatus(sourceSyncDetails.documents, isSyncingDocuments)}
                </div>
                {canSync && (
                  <div className="kb-tab-action-area">
                    <Button
                      text="Sync Documents"
                      icon="fas fa-sync-alt"
                      type="default"
                      stylingMode="contained"
                      disabled={isSyncBusy}
                      onClick={handleSyncDocuments}
                    />
                  </div>
                )}
              </div>

              {isSyncingDocuments ? (
                renderSyncProgressCard(documentProgressData)
              ) : (
                <DataGrid
                  dataSource={enrichedFiles}
                  showBorders={true}
                  showRowLines={true}
                  rowAlternationEnabled={true}
                  columnAutoWidth={false}
                  columnHidingEnabled={true}
                  wordWrapEnabled={true}
                  hoverStateEnabled={true}
                  width="100%"
                  className="kb-data-grid"
                  noDataText={isLoadingFiles ? 'Loading documents...' : 'No documents found in Knowledge Base'}
                >
                  <LoadPanel enabled={isLoadingFiles} />
                  <Sorting mode="multiple" />
                  <HeaderFilter visible={true} />
                  <SearchPanel visible={true} placeholder="Search documents..." width={260} />
                  <Paging defaultPageSize={25} />
                  <Pager
                    showPageSizeSelector={true}
                    allowedPageSizes={[10, 25, 50, 100]}
                    showInfo={true}
                    showNavigationButtons={true}
                  />
                  <DGToolbar>
                    <DGItem location="before">
                      <div className="kb-grid-title-info">
                        <span className="kb-grid-title">Documents</span>
                        <span className="kb-grid-count">({enrichedFiles.length} {enrichedFiles.length === 1 ? 'file' : 'files'})</span>
                      </div>
                    </DGItem>
                    <DGItem location="after">
                      <Button
                        icon="fas fa-sync-alt"
                        hint="Refresh document list"
                        stylingMode="text"
                        onClick={handleRefreshAll}
                        disabled={isLoadingFiles}
                      />
                    </DGItem>
                    <DGItem name="searchPanel" location="after" />
                  </DGToolbar>

                  <Column
                    dataField="name"
                    caption="Document Name"
                    cellRender={nameCellRender}
                    minWidth={300}
                    hidingPriority={6}
                  />
                  <Column
                    dataField="folder"
                    caption="Folder"
                    cellRender={folderCellRender}
                    width={250}
                    hidingPriority={3}
                  />
                  <Column
                    dataField="computedStatusLabel"
                    caption="Ingestion Status"
                    cellRender={statusCellRender}
                    calculateSortValue={(data) => (data.computedStatus === 'FAILED' ? 0 : 1)}
                    defaultSortOrder="asc"
                    defaultSortIndex={0}
                    width={140}
                    alignment="center"
                    hidingPriority={4}
                  />
                  <Column
                    dataField="lastModified"
                    caption="Last Modified"
                    dataType="datetime"
                    format="shortDateShortTime"
                    defaultSortOrder="desc"
                    defaultSortIndex={1}
                    cellRender={lastModifiedCellRender}
                    width={120}
                    alignment="left"
                    hidingPriority={2}
                  />
                  <Column
                    dataField="size"
                    caption="Size"
                    dataType="number"
                    cellRender={sizeCellRender}
                    width={85}
                    alignment="right"
                    hidingPriority={1}
                  />
                  <Column
                    caption="Actions"
                    cellRender={actionCellRender}
                    width={75}
                    alignment="center"
                    allowSorting={false}
                    allowFiltering={false}
                    hidingPriority={5}
                  />
                </DataGrid>
              )}
          </div>
        ) : (
          <div className="kb-tab-content">
            <div className="kb-tab-toolbar">
              <div className="kb-tab-status-area">
                {renderTabSyncStatus(sourceSyncDetails.website, isSyncingWebsite)}
              </div>
              {canSync && (
                <div className="kb-tab-action-area">
                  <Button
                    text="Sync Website"
                    icon="fas fa-sync-alt"
                    type="default"
                    stylingMode="contained"
                    disabled={isSyncBusy}
                    onClick={handleSyncWebsite}
                  />
                </div>
              )}
            </div>

            {isSyncingWebsite ? (
              renderSyncProgressCard(websiteProgressData)
            ) : (
              <DataGrid
                dataSource={enrichedWebsiteLinks}
                showBorders={true}
                showRowLines={true}
                rowAlternationEnabled={true}
                columnAutoWidth={false}
                columnHidingEnabled={true}
                wordWrapEnabled={true}
                hoverStateEnabled={true}
                width="100%"
                className="kb-data-grid"
                noDataText="No website links indexed"
              >
                <Sorting mode="multiple" />
                <HeaderFilter visible={true} />
                <SearchPanel visible={true} placeholder="Search website links..." width={260} />
                <Paging defaultPageSize={25} />
                <Pager
                  showPageSizeSelector={true}
                  allowedPageSizes={[10, 25, 50, 100]}
                  showInfo={true}
                  showNavigationButtons={true}
                />
                <DGToolbar>
                  <DGItem location="before">
                    <div className="kb-grid-title-info">
                      <span className="kb-grid-title">Website Data Source</span>
                      <span className="kb-grid-count">({enrichedWebsiteLinks.length} {enrichedWebsiteLinks.length === 1 ? 'link' : 'links'})</span>
                    </div>
                  </DGItem>
                  <DGItem location="after">
                    <Button
                      icon="fas fa-sync-alt"
                      hint="Refresh website links"
                      stylingMode="text"
                      onClick={handleRefreshAll}
                    />
                  </DGItem>
                  <DGItem name="searchPanel" location="after" />
                </DGToolbar>

                <Column
                  dataField="url"
                  caption="Page Link / Resource"
                  cellRender={urlCellRender}
                  calculateCellValue={(data) => `${data.title || ''} ${data.url || ''}`}
                  calculateSortValue={(data) => data.title || data.url}
                  minWidth={320}
                  hidingPriority={6}
                />
                <Column
                  dataField="domain"
                  caption="Domain"
                  cellRender={domainCellRender}
                  width={180}
                  hidingPriority={4}
                />
                <Column
                  dataField="computedStatusLabel"
                  caption="Ingestion Status"
                  cellRender={statusCellRender}
                  calculateSortValue={(data) => (data.computedStatus === 'FAILED' ? 0 : 1)}
                  defaultSortOrder="asc"
                  defaultSortIndex={0}
                  width={140}
                  alignment="center"
                  hidingPriority={5}
                />
                <Column
                  dataField="lastModified"
                  caption="Last Crawled"
                  dataType="datetime"
                  format="shortDateShortTime"
                  defaultSortOrder="desc"
                  defaultSortIndex={1}
                  cellRender={lastModifiedCellRender}
                  width={140}
                  alignment="left"
                  hidingPriority={2}
                />
                <Column
                  caption="Actions"
                  cellRender={webActionCellRender}
                  width={75}
                  alignment="center"
                  allowSorting={false}
                  allowFiltering={false}
                  hidingPriority={1}
                />
              </DataGrid>
            )}
          </div>
        )}
      </div>
    )}


      {selectedFailedDoc && (
        <Popup
          visible={true}
          onHiding={() => setSelectedFailedDoc(null)}
          dragEnabled={true}
          hideOnOutsideClick={true}
          showCloseButton={true}
          showTitle={true}
          title={selectedFailedDoc.type === 'web' || !!selectedFailedDoc.url ? "Website Link Ingestion Error" : "Document Ingestion Error"}
          container=".dx-viewport"
          width={580}
          height="auto"
          contentRender={renderDocErrorContent}
        />
      )}
    </div>
  );
}
